import { createContext, useContext } from "react";

import { ThemeBuilderType, smartrent } from "@smartrent/tokens";

import {
  ConfigType,
  ProspectInfoConfigType,
  ProspectType,
  StatusType,
  TourType,
} from "@/types";

export type unitFilterType = {
  limit?: number | null;
  page?: number | null;
  minSquareFeet?: number | null;
  maxSquareFeet?: number | null;
  minRent?: number | null;
  maxRent?: number | null;
  minBeds?: number | null;
  maxBeds?: number | null;
  minBaths?: number | null;
  maxBaths?: number | null;
  dir?: "asc" | "desc" | null;
  sort?: string | null;
};

// @todo, there are runtime errors that can occur because we
// assume the `config`, `prospect`, and `tour` are always loaded.
// We need to refactor to handle when these values are null or undefined
// so that typescript can warn us when we haven't handled a scenario
// where the data isn't loaded or won't be loaded
export type StateType = {
  config: ConfigType;
  status: StatusType;
  tourRescheduled: boolean;
  hasProspect: null | boolean;
  prospect: ProspectType;
  prospectInfoConfig: ProspectInfoConfigType;
  tour?: TourType;
  theme: ThemeBuilderType;
  accessTokens: { [groupId: string]: string };
  setAccessTokens: (accessTokens: Object) => void;
  prospectVerificationCodeCreatedAt: Date | undefined;
  unitFilters: unitFilterType;
};

const config: ConfigType = {
  blockInternationalPhoneNumbers: false,
  enableClickToUnlock: false,
  allowPassportVerification: false,
  customSurveyLink: null,
  surveyEnabled: true,
  applicationLinkUrl: null,
  uuid: null,
  disableDoubleBookings: false,
  groupId: null,
  propertyName: null,
  propertyImageUrl: null,
  propertyLogoUrl: null,
  timezone: null,
  imageUrl: null,
  maxUnits: null,
  tourAllEnabled: false,
  color: null,
  blockedProspectMessage: null,
  prospectJoinWaitlistMessage: "",
  phoneNumber: null,
  mondayStart: null,
  mondayEnd: null,
  tuesdayStart: null,
  tuesdayEnd: null,
  wednesdayStart: null,
  wednesdayEnd: null,
  thursdayStart: null,
  thursdayEnd: null,
  fridayStart: null,
  fridayEnd: null,
  saturdayStart: null,
  saturdayEnd: null,
  sundayStart: null,
  sundayEnd: null,
  indemnityAgreementUrl: null,
  industry: null,
  tourDuration: null,
  tourEndBuffer: null,
  accessCodeEarlyAccess: null,
  welcomeMessage: null,
  welcomeVideoLink: null,
  alwaysShowWelcomeMessage: false,
  endTourMessage: null,
  endOfTourVideoLink: null,
  communityMap: null,
  engrainMap: null,
  engrainAssetId: null,
  engrainFloorData: [],
  engrainPricingProcessId: null,
  engrainUnitMapId: null,
  engrainUnitMapsEnabled: false,
  showUnitAddress: false,
  skipIdVerification: false,
  geofenceRadiusMiles: 3,
  waitListEnabled: false,
  showBleAccessOnCommunityAccessCard: null,
  rentRollEnabled: false,
  scammerAlertMessagesEnabled: false,
  scammerAlertMessages: null,
  country: null,
  covidAgreementEnabled: false,
  funnelSMSOptIn: false,
  tourScheduleAdvanceDays: 7,
};

export const initialState: StateType = {
  config,
  status: "pending",
  prospect: {
    id: null,
    uuid: null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
    email: null,
    streetAddress1: null,
    streetAddress2: null,
    city: null,
    state: null,
    status: null,
    zipCode: null,
    desiredMoveInDate: null,
    desiredNumberOfBedrooms: null,
    realtorInfo: null,
    isRealEstateAgent: null,
    waitListOptedInAt: null,
    allowSmsNotifications: null,
  },
  prospectInfoConfig: {
    prospectAddressEnabled: false,
    prospectAddressRequired: false,
    prospectCityEnabled: false,
    prospectCityRequired: false,
    prospectStateEnabled: false,
    prospectStateRequired: false,
    prospectZipCodeEnabled: false,
    prospectZipCodeRequired: false,
    prospectDesiredMoveInDateEnabled: false,
    prospectDesiredMoveInDateRequired: false,
    prospectDesiredNumberOfBedroomsEnabled: false,
    prospectDesiredNumberOfBedroomsRequired: false,
    realtorInfoEnabled: false,
    isRealEstateAgentEnabled: false,
    realtorInstructions: "",
  },
  hasProspect: null,
  tourRescheduled: false,
  theme: smartrent,
  accessTokens: {}, // {[groupId]: jwt}
  setAccessTokens: () => {},
  prospectVerificationCodeCreatedAt: undefined,
  unitFilters: {},
};
export type StateContextType = [StateType, Function];
export const StateContext = createContext<StateContextType>([
  initialState,
  () => {},
]);
export const useAppState = () => useContext<StateContextType>(StateContext);
